"use client";

import { cn } from "@/lib/utils";

import { useCalendarContext } from "../calendar-context";
import { getEventColorClasses } from "../event-colors";
import { type PositionedEvent } from "../event-layout";
import { formatTime } from "../utils";

import { EventPopover } from "./event-popover";

// =============================================================================

// Under this visual duration the two-line layout would clip the time
const COMPACT_BELOW_MINUTES = 45;

type EventBlockProps = {
  positioned: PositionedEvent;
  /** Minutes covered by the column (percentages → minutes) */
  totalMinutes: number;
};

/** Timed event positioned in a day column of the week view */
export function EventBlock({ positioned, totalMinutes }: EventBlockProps) {
  const { locale } = useCalendarContext();
  const { event, top, height, left, width, isStart, isEnd } = positioned;

  const isCompact = (height / 100) * totalMinutes < COMPACT_BELOW_MINUTES;
  // Multi-day segments: start on the first day, end on the last, nothing between
  const timeLabel =
    isStart && isEnd
      ? `${formatTime(event.start, locale)} – ${formatTime(event.end, locale)}`
      : isStart
        ? formatTime(event.start, locale)
        : isEnd
          ? `→ ${formatTime(event.end, locale)}`
          : "";

  return (
    <EventPopover event={event}>
      <button
        type="button"
        className={cn(
          "absolute z-2 min-w-0 overflow-hidden rounded-md border px-1.5 text-left transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
          isCompact ? "flex items-center gap-1.5" : "py-1",
          // Squared corners: the segment continues on another day
          !isStart && "rounded-t-none",
          !isEnd && "rounded-b-none",
          getEventColorClasses(event.color).base
        )}
        style={{
          top: `${top}%`,
          height: `${height}%`,
          left: `${left}%`,
          // Small gap so side by side events stay apart
          width: `calc(${width}% - 2px)`,
        }}
      >
        <span
          className={cn(
            "truncate text-xs leading-4 font-medium",
            !isCompact && "block"
          )}
        >
          {event.title}
        </span>
        {timeLabel && (
          <span
            className={cn(
              "truncate text-xs leading-4 opacity-80",
              isCompact ? "shrink-0" : "block"
            )}
          >
            {timeLabel}
          </span>
        )}
      </button>
    </EventPopover>
  );
}
